import { Link } from 'react-router-dom';
import { Badge, type BadgeVariant } from '../../components/ui/badge';
import type { Broadcast, BroadcastStatus } from '../../types/broadcast';
import type { BroadcastSort, BroadcastSortKey } from '../../lib/broadcastListView';
import { brandLabel } from './audienceDescription';

// Stage 6A v6 — shared broadcast table, reused by the active list (BroadcastsPage)
// and the admin recovery view (DeletedBroadcastsPage) via a `mode` discriminator.
// Active mode shows Edit/View + a permission-gated Delete; deleted mode swaps in
// Deleted-at / Deleted-by columns and a Restore action.
// v7 — active mode adds a sortable "Sent" column and clickable sort headers
// (parent owns the sort state); deleted mode is unchanged.

const STATUS_VARIANT: Record<BroadcastStatus, BadgeVariant> = {
  draft: 'info',
  sending: 'warning',
  sent: 'neutral',
  completed: 'success',
  failed: 'danger',
};

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString('en-AU', { dateStyle: 'medium', timeStyle: 'short' });
}

type ActiveProps = {
  mode: 'active';
  broadcasts: Broadcast[];
  brandName: (id: string) => string;
  canDelete: (b: Broadcast) => boolean;
  onDelete: (b: Broadcast) => void;
  sort: BroadcastSort;
  onSort: (key: BroadcastSortKey) => void;
};
type DeletedProps = {
  mode: 'deleted';
  broadcasts: Broadcast[];
  brandName: (id: string) => string;
  onRestore: (b: Broadcast) => void;
  restoringId: string | null;
};
type Props = ActiveProps | DeletedProps;

// Clickable sort header (active mode). Shows ▲/▼ on the active column, ↕ otherwise.
function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
}: {
  label: string;
  sortKey: BroadcastSortKey;
  sort: BroadcastSort;
  onSort: (key: BroadcastSortKey) => void;
}) {
  const active = sort.key === sortKey;
  return (
    <th
      className="px-4 py-2 font-medium"
      aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className="inline-flex items-center gap-1 uppercase tracking-wide hover:text-slate-700"
      >
        <span>{label}</span>
        <span aria-hidden className="text-[10px] text-slate-400">
          {active ? (sort.dir === 'asc' ? '▲' : '▼') : '↕'}
        </span>
      </button>
    </th>
  );
}

export function BroadcastTable(props: Props) {
  const { broadcasts, brandName, mode } = props;
  return (
    <div className="overflow-hidden rounded-md border border-slate-200">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
          <tr>
            {mode === 'active' ? (
              <>
                <SortHeader label="Title" sortKey="title" sort={props.sort} onSort={props.onSort} />
                <SortHeader label="Brand" sortKey="brand" sort={props.sort} onSort={props.onSort} />
                <th className="px-4 py-2 font-medium">Channels</th>
                <SortHeader label="Status" sortKey="status" sort={props.sort} onSort={props.onSort} />
                <SortHeader label="Sent" sortKey="sentAt" sort={props.sort} onSort={props.onSort} />
                <SortHeader
                  label="Recipients"
                  sortKey="recipients"
                  sort={props.sort}
                  onSort={props.onSort}
                />
                <th className="px-4 py-2" />
              </>
            ) : (
              <>
                <th className="px-4 py-2 font-medium">Title</th>
                <th className="px-4 py-2 font-medium">Brand</th>
                <th className="px-4 py-2 font-medium">Channels</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 font-medium">Recipients</th>
                <th className="px-4 py-2 font-medium">Deleted</th>
                <th className="px-4 py-2 font-medium">Deleted by</th>
                <th className="px-4 py-2" />
              </>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {broadcasts.map((b) => (
            <tr key={b.id} className="hover:bg-slate-50">
              <td className="px-4 py-2 font-medium text-slate-900">{b.title}</td>
              <td className="px-4 py-2 text-slate-600">{brandLabel(b.audienceScope, brandName)}</td>
              <td className="px-4 py-2 text-slate-600">{b.channels.join(' + ') || '—'}</td>
              <td className="px-4 py-2">
                <Badge variant={STATUS_VARIANT[b.status]}>{b.status}</Badge>
              </td>
              {mode === 'active' && (
                <td className="px-4 py-2 text-slate-600">{fmtDate(b.sentAt)}</td>
              )}
              <td className="px-4 py-2 text-slate-600">{b.totalRecipients}</td>
              {mode === 'deleted' ? (
                <>
                  <td className="px-4 py-2 text-slate-600">{fmtDate(b.deletedAt)}</td>
                  <td className="px-4 py-2 text-slate-600">{b.deletedBy?.name ?? '—'}</td>
                  <td className="px-4 py-2 text-right">
                    <button
                      type="button"
                      onClick={() => props.onRestore(b)}
                      disabled={props.restoringId === b.id}
                      className="text-sm text-sky-700 underline hover:text-sky-900 disabled:opacity-50"
                    >
                      {props.restoringId === b.id ? 'Restoring…' : 'Restore'}
                    </button>
                  </td>
                </>
              ) : (
                <td className="px-4 py-2 text-right">
                  <div className="flex items-center justify-end gap-3">
                    <Link
                      to={b.status === 'draft' ? `/broadcasts/${b.id}/edit` : `/broadcasts/${b.id}`}
                      className="text-sm text-slate-600 underline hover:text-slate-900"
                    >
                      {b.status === 'draft' ? 'Edit' : 'View'}
                    </Link>
                    {props.canDelete(b) && (
                      <button
                        type="button"
                        onClick={() => props.onDelete(b)}
                        className="text-sm text-red-600 underline hover:text-red-800"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
